import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { isSignInWithEmailLink, onAuthStateChanged, signInWithCustomToken, signOut, type User } from 'firebase/auth';
import { auth, call, errText } from './firebase';
import { VerifyPhone, formatPhone } from './verify';

// jobs.sudshamper.com (requirement/connect-marketplace.md → Jobs).
//   /             open laundry jobs from suds Connect members and vendors
//   /job/<id>     one job (schema.org JobPosting for Google for Jobs) + apply form
//   /available    job seekers: free "available for work" profile; sign in with an
//                 emailed code, then verify a phone (functions/src/webIdentity.ts)
// Paths, not hashes, so job pages can be indexed (deploy/.htaccess serves the app for all).

const STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ');
const EMPLOYMENT: Record<string, string> = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', temporary: 'Temporary' };
const ROLES: Record<string, string> = { attendant: 'Attendant', wdf: 'Wash-dry-fold', delivery_driver: 'Delivery driver', technician: 'Technician', manager: 'Manager', cleaner: 'Cleaner', other: 'Other' };
const SCHEMA_EMPLOYMENT: Record<string, string> = { full_time: 'FULL_TIME', part_time: 'PART_TIME', contract: 'CONTRACTOR', temporary: 'TEMPORARY' };

type Job = {
  id: string; title: string; description: string; role: string; employment: string; payMinCents: number | null; payMaxCents: number | null;
  payUnit: string; city: string; state: string; employer: string; employerLogo: string | null; photo: string | null; postedAt: string; endsAt: string;
};

function pay(j: Job): string {
  if (j.payMinCents == null && j.payMaxCents == null) return '';
  const f = (c: number) => `$${(c / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  const unit = { hour: '/hr', week: '/wk', month: '/mo', year: '/yr' }[j.payUnit] ?? '';
  return j.payMinCents != null && j.payMaxCents != null && j.payMaxCents !== j.payMinCents ? `${f(j.payMinCents)}–${f(j.payMaxCents)}${unit}` : `${f((j.payMinCents ?? j.payMaxCents)!)}${unit}`;
}

function usePath(): [string, (p: string) => void] {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => { const h = () => setPath(window.location.pathname); window.addEventListener('popstate', h); return () => window.removeEventListener('popstate', h); }, []);
  const go = useCallback((p: string) => { window.history.pushState({}, '', p); setPath(p); window.scrollTo(0, 0); }, []);
  return [path, go];
}

export default function App() {
  const [path, go] = usePath();
  const link = (p: string) => (e: React.MouseEvent) => { e.preventDefault(); go(p); };
  const jobId = /^\/job\/([A-Za-z0-9_-]{6,40})\/?$/.exec(path)?.[1];
  return (
    <>
      <div className="top">
        <a href="/" onClick={link('/')}><img src="/wordmark.png" alt="suds" /></a>
        <b>Jobs</b>
        <span className="sp" />
        <a href="/available" onClick={link('/available')} style={{ color: '#cbd5e1', fontWeight: 700 }}>Looking for work?</a>
      </div>
      {jobId ? <JobPage id={jobId} go={go} /> : path.startsWith('/available') ? <Available /> : <JobList go={go} />}
      <div className="wrap muted">Jobs at laundromats and laundry businesses on suds Connect. A Shrestha Ventures service.</div>
    </>
  );
}

function JobList({ go }: { go: (p: string) => void }) {
  const [state, setState] = useState(() => new URLSearchParams(window.location.search).get('state') ?? '');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    let live = true;
    document.title = state ? `Laundry jobs in ${state} | suds Jobs` : 'Laundry jobs near you | suds Jobs';
    setJobs(null);
    setErr(null);
    call('jobsList', { state }).then((r) => { if (live) { setJobs(r.jobs); setNext(r.next); } }).catch((e) => { if (live) setErr(errText(e)); });
    return () => { live = false; };
  }, [state]);
  const [loadingMore, setLoadingMore] = useState(false);
  const moreRef = useRef(false);
  // One page at a time: a double-click must not append the same page twice.
  const more = async () => {
    if (moreRef.current || !next) return;
    moreRef.current = true; setLoadingMore(true);
    const forState = state;
    try {
      const r = await call('jobsList', { state, after: next });
      if (forState === stateRef.current) { setJobs((j) => [...(j ?? []), ...r.jobs]); setNext(r.next); }
    } catch (e) { setErr(errText(e)); } finally { moreRef.current = false; setLoadingMore(false); }
  };
  return (
    <>
      <div className="hero">
        <div className="wrap" style={{ padding: 0 }}>
          <h1>Laundry jobs near you</h1>
          <p>Attendant, wash-dry-fold, delivery and technician jobs at laundromats and laundry businesses. Apply in a minute; no account needed.</p>
          <select aria-label="Filter jobs by state" value={state} onChange={(e) => { setState(e.target.value); window.history.replaceState({}, '', e.target.value ? `/?state=${e.target.value}` : '/'); }} style={{ maxWidth: 220 }}>
            <option value="">All states</option>{STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="wrap">
        {err ? <div className="err">{err}</div> : null}
        {jobs === null ? <p className="muted">Loading jobs…</p> : jobs.length === 0 ? (
          <div className="card"><h2>No open jobs{state ? ` in ${state}` : ''} right now</h2><p className="muted">Post that you're available and owners near you can reach out.</p><button onClick={() => go('/available')}>I'm looking for work</button></div>
        ) : jobs.map((j) => (
          <a key={j.id} className="job" href={`/job/${j.id}`} onClick={(e) => { e.preventDefault(); go(`/job/${j.id}`); }}>
            <div className="t">{j.title}</div>
            <div className="pay">{[j.role, EMPLOYMENT[j.employment], pay(j)].filter(Boolean).join(' · ')}</div>
            <div className="muted">{j.employer} · {[j.city, j.state].filter(Boolean).join(', ')} · posted {new Date(j.postedAt).toLocaleDateString()}</div>
          </a>
        ))}
        {next ? <button className="secondary" onClick={more} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'More jobs'}</button> : null}
      </div>
    </>
  );
}

/** A JobPosting script tag in <head>, so Google for Jobs can list the job. */
function useJobPostingSchema(j: Job | null) {
  useEffect(() => {
    if (!j) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    const unit = ({ hour: 'HOUR', week: 'WEEK', month: 'MONTH', year: 'YEAR' } as Record<string, string>)[j.payUnit] ?? 'HOUR';
    el.text = JSON.stringify({
      '@context': 'https://schema.org/', '@type': 'JobPosting',
      title: j.title, description: j.description || j.title,
      datePosted: j.postedAt, validThrough: j.endsAt, employmentType: SCHEMA_EMPLOYMENT[j.employment] ?? 'OTHER',
      hiringOrganization: { '@type': 'Organization', name: j.employer, ...(j.employerLogo ? { logo: j.employerLogo } : {}) },
      jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: j.city, addressRegion: j.state, addressCountry: 'US' } },
      ...(j.payMinCents != null || j.payMaxCents != null ? {
        baseSalary: { '@type': 'MonetaryAmount', currency: 'USD', value: { '@type': 'QuantitativeValue', unitText: unit,
          ...(j.payMinCents != null ? { minValue: j.payMinCents / 100 } : {}), ...(j.payMaxCents != null ? { maxValue: j.payMaxCents / 100 } : {}) } },
      } : {}),
      directApply: true,
      identifier: { '@type': 'PropertyValue', name: 'suds Jobs', value: j.id },
      url: `https://jobs.sudshamper.com/job/${j.id}`,
    });
    document.head.appendChild(el);
    document.title = `${j.title} at ${j.employer}, ${j.city} ${j.state} | suds Jobs`;
    return () => { el.remove(); };
  }, [j]);
}

// What jobsApply accepts (functions/src/marketJobs.ts parseAttachment checks the bytes).
const ATTACH_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png';
const ATTACH_MAX = 5 * 1024 * 1024;

function readAttachment(file: File): Promise<{ name: string; base64: string }> {
  if (file.size > ATTACH_MAX) return Promise.reject(new Error('That file is too big. Attach one under 5 MB.'));
  if (!/\.(pdf|docx?|jpe?g|png)$/i.test(file.name)) return Promise.reject(new Error('Attach a PDF, Word document, JPG or PNG.'));
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, base64: String(r.result).replace(/^data:[^;]*;base64,/, '') });
    r.onerror = () => reject(new Error("We couldn't read that file. Try another."));
    r.readAsDataURL(file);
  });
}

// ── The applicant's basics ─────────────────────────────────────────────────
const AVAILABILITY: [string, string][] = [
  ['weekdays', 'Weekdays'], ['weekends', 'Weekends'], ['mornings', 'Mornings'],
  ['afternoons', 'Afternoons'], ['evenings', 'Evenings'], ['overnight', 'Overnight'],
];
const HOURS: [string, string][] = [['full_time', 'Full-time'], ['part_time', 'Part-time'], ['either', 'Either']];

type ApplicantForm = {
  street: string; city: string; state: string; zip: string;
  startDate: string; availability: string[]; hours: string;
  noPriorJob: boolean; employer: string; position: string; from: string; to: string; current: boolean;
  adult: boolean | null; workAuthorized: boolean | null;
};
const BLANK_APPLICANT: ApplicantForm = {
  street: '', city: '', state: '', zip: '', startDate: '', availability: [], hours: '',
  noPriorJob: false, employer: '', position: '', from: '', to: '', current: false, adult: null, workAuthorized: null,
};
const localYmd = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const YM = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The first thing still missing, in the server's words; null when it can be sent. */
function applicantProblem(a: ApplicantForm): string | null {
  if (a.street.trim().length < 3) return 'Enter your street address.';
  if (a.city.trim().length < 2) return 'Enter your city.';
  if (!a.state) return 'Choose your state.';
  if (a.zip.replace(/\D/g, '').length !== 5) return 'Enter your 5-digit ZIP code.';
  if (!a.startDate) return 'Choose the earliest date you can start.';
  if (!a.availability.length) return "Choose when you're available.";
  if (!a.hours) return 'Choose full-time, part-time or either.';
  if (!a.noPriorJob) {
    if (a.employer.trim().length < 2) return 'Enter your most recent employer, or tick "No previous job".';
    if (a.position.trim().length < 2) return 'Enter the position you held.';
    if (!YM.test(a.from)) return 'Enter when you started that job.';
    if (!a.current && (!YM.test(a.to) || a.to < a.from)) return 'Enter when that job ended, or tick "I still work here".';
  }
  if (a.adult === null) return "Answer whether you're 18 or older.";
  if (a.workAuthorized === null) return "Answer whether you're authorized to work in the US.";
  return null;
}

function applicantPayload(a: ApplicantForm) {
  return {
    address: { street: a.street, city: a.city, state: a.state, zip: a.zip },
    startDate: a.startDate, availability: a.availability, hours: a.hours,
    noPriorJob: a.noPriorJob,
    ...(a.noPriorJob ? {} : { lastJob: { employer: a.employer, position: a.position, from: a.from, to: a.current ? null : a.to, current: a.current } }),
    adult: a.adult, workAuthorized: a.workAuthorized,
  };
}

function YesNo({ label, value, onChange }: { label: string; value: boolean | null; onChange: (v: boolean) => void }) {
  const id = useId();
  return (
    <>
      <label id={id}>{label}</label>
      <div className="chips" role="radiogroup" aria-labelledby={id}>
        {([[true, 'Yes'], [false, 'No']] as const).map(([v, t]) => (
          <button key={t} type="button" role="radio" aria-checked={value === v} className={`chip ${value === v ? 'on' : ''}`} onClick={() => onChange(v)}>{t}</button>
        ))}
      </div>
    </>
  );
}

function ApplicantFields({ ap, setAp, uid }: { ap: ApplicantForm; setAp: (f: (a: ApplicantForm) => ApplicantForm) => void; uid: string }) {
  const set = (patch: Partial<ApplicantForm>) => setAp((a) => ({ ...a, ...patch }));
  const id = (k: string) => `${uid}ap_${k}`;
  const thisMonth = localYmd().slice(0, 7);
  return (
    <>
      <label htmlFor={id('street')}>Street address</label>
      <input id={id('street')} aria-required="true" value={ap.street} onChange={(e) => set({ street: e.target.value })} autoComplete="street-address" />
      <div className="row">
        <div><label htmlFor={id('city')}>City</label><input id={id('city')} aria-required="true" value={ap.city} onChange={(e) => set({ city: e.target.value })} autoComplete="address-level2" /></div>
        <div><label htmlFor={id('state')}>State</label><select id={id('state')} aria-required="true" value={ap.state} onChange={(e) => set({ state: e.target.value })} autoComplete="address-level1"><option value="">—</option>{STATES.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div><label htmlFor={id('zip')}>ZIP code</label><input id={id('zip')} aria-required="true" inputMode="numeric" maxLength={5} value={ap.zip} onChange={(e) => set({ zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} autoComplete="postal-code" /></div>
      </div>

      <label htmlFor={id('start')}>Earliest start date</label>
      <input id={id('start')} aria-required="true" type="date" min={localYmd()} value={ap.startDate} onChange={(e) => set({ startDate: e.target.value })} />

      <label id={id('avail')}>When you're available</label>
      <div className="chips" role="group" aria-labelledby={id('avail')}>
        {AVAILABILITY.map(([k, t]) => (
          <button key={k} type="button" aria-pressed={ap.availability.includes(k)} className={`chip ${ap.availability.includes(k) ? 'on' : ''}`}
            onClick={() => set({ availability: ap.availability.includes(k) ? ap.availability.filter((x) => x !== k) : [...ap.availability, k] })}>{t}</button>
        ))}
      </div>
      <label id={id('hours')}>Hours you want</label>
      <div className="chips" role="radiogroup" aria-labelledby={id('hours')}>
        {HOURS.map(([k, t]) => (
          <button key={k} type="button" role="radio" aria-checked={ap.hours === k} className={`chip ${ap.hours === k ? 'on' : ''}`} onClick={() => set({ hours: k })}>{t}</button>
        ))}
      </div>

      <label id={id('job')} style={{ marginTop: 18 }}>Most recent job</label>
      <label style={{ fontWeight: 600, margin: '4px 0' }}>
        <input type="checkbox" checked={ap.noPriorJob} onChange={(e) => set({ noPriorJob: e.target.checked })} style={{ width: 'auto', marginRight: 8 }} />
        No previous job
      </label>
      {!ap.noPriorJob ? (
        <>
          <div className="row">
            <div><label htmlFor={id('employer')}>Employer</label><input id={id('employer')} aria-required="true" value={ap.employer} onChange={(e) => set({ employer: e.target.value })} autoComplete="organization" /></div>
            <div><label htmlFor={id('position')}>Position</label><input id={id('position')} aria-required="true" value={ap.position} onChange={(e) => set({ position: e.target.value })} autoComplete="organization-title" /></div>
          </div>
          <div className="row">
            <div><label htmlFor={id('from')}>Started (month)</label><input id={id('from')} aria-required="true" type="month" max={thisMonth} placeholder="2023-04" value={ap.from} onChange={(e) => set({ from: e.target.value })} /></div>
            {!ap.current ? (
              <div><label htmlFor={id('to')}>Ended (month)</label><input id={id('to')} aria-required="true" type="month" min={ap.from || undefined} max={thisMonth} placeholder="2025-08" value={ap.to} onChange={(e) => set({ to: e.target.value })} /></div>
            ) : null}
          </div>
          <label style={{ fontWeight: 600, margin: '8px 0 0' }}>
            <input type="checkbox" checked={ap.current} onChange={(e) => set({ current: e.target.checked })} style={{ width: 'auto', marginRight: 8 }} />
            I still work here
          </label>
        </>
      ) : null}

      <YesNo label="Are you 18 or older?" value={ap.adult} onChange={(v) => set({ adult: v })} />
      <YesNo label="Are you legally authorized to work in the US?" value={ap.workAuthorized} onChange={(v) => set({ workAuthorized: v })} />
    </>
  );
}

function JobPage({ id, go }: { id: string; go: (p: string) => void }) {
  // undefined: loading; null: closed or missing (jobsGet said not-found); 'error': couldn't load.
  const [job, setJob] = useState<Job | null | 'error' | undefined>(undefined);
  const [f, setF] = useState({ name: '', email: '', phone: '', message: '', hp_x9: '' });
  // The applicant's basics (functions/src/marketJobs.ts cleanApplicant checks them again).
  const [ap, setAp] = useState<ApplicantForm>(BLANK_APPLICANT);
  // One optional file (a résumé), read in the browser and sent with the application.
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  // The latest pick wins: a slow read of an earlier file must not land after it.
  const pickSeq = useRef(0);
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const uid = useId();
  const load = useCallback(() => {
    let live = true;
    setJob(undefined);
    call('jobsGet', { id }).then((r) => { if (live) setJob(r.job); }).catch((e) => {
      if (!live) return;
      // Only the server's "no such open job" is a closed job; a network or server error is not.
      const code = String(e?.code ?? '');
      setJob(code === 'functions/not-found' || code === 'functions/invalid-argument' ? null : 'error');
    });
    return () => { live = false; };
  }, [id]);
  useEffect(() => load(), [load]);
  useJobPostingSchema(job && job !== 'error' ? job : null);
  // A closed job answers 200 (an SPA); noindex so search engines drop it instead of keeping
  // a soft 404. Only for a job the server says is closed: an outage must not deindex open jobs.
  useEffect(() => {
    if (job !== null) return;
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex';
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, [job]);
  if (job === undefined) return <div className="wrap muted">Loading…</div>;
  if (job === 'error') return <div className="wrap"><div className="card"><h2>We couldn't load this job</h2><p className="muted">Check your connection and try again.</p><button onClick={() => load()}>Try again</button></div></div>;
  if (job === null) return <div className="wrap"><div className="card"><h2>This job is no longer open</h2><button onClick={() => go('/')}>See open jobs</button></div></div>;
  const apply = async () => {
    if (state !== 'idle') return;
    setState('sending'); setErr(null);
    try {
      await call('jobsApply', { listingId: id, ...f, applicant: applicantPayload(ap), ...(file ? { attachment: file } : {}) });
      setState('sent');
    } catch (e) { setErr(errText(e)); setState('idle'); }
  };
  return (
    <div className="wrap">
      <a href="/" onClick={(e) => { e.preventDefault(); go('/'); }}>← All jobs</a>
      <div className="grid2" style={{ marginTop: 14 }}>
        <div className="card">
          {job.photo ? <img src={job.photo} alt="" style={{ width: '100%', borderRadius: 12, marginBottom: 14 }} /> : null}
          <h1>{job.title}</h1>
          <div style={{ color: 'var(--aquaDark)', fontWeight: 700 }}>{[job.role, EMPLOYMENT[job.employment], pay(job)].filter(Boolean).join(' · ')}</div>
          <p className="muted">{job.employer} · {[job.city, job.state].filter(Boolean).join(', ')} · posted {new Date(job.postedAt).toLocaleDateString()}</p>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{job.description}</p>
        </div>
        <div className="card">
          {state === 'sent' ? (
            <><h2>Application sent</h2><p className="muted">{job.employer} has your details and will contact you directly if it's a fit. We've emailed you a copy at {f.email}.</p></>
          ) : (
            <>
              <h2>Apply</h2>
              <p className="muted">Your details go only to this employer.</p>
              <label htmlFor={`${uid}name`}>Name</label><input id={`${uid}name`} aria-required="true" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoComplete="name" />
              <label htmlFor={`${uid}email`}>Email</label><input id={`${uid}email`} aria-required="true" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="email" />
              <label htmlFor={`${uid}phone`}>Phone</label><input id={`${uid}phone`} aria-required="true" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} autoComplete="tel" />
              <ApplicantFields ap={ap} setAp={setAp} uid={uid} />
              <label htmlFor={`${uid}msg`}>Anything else? (optional)</label><textarea id={`${uid}msg`} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Experience, languages, anything the employer should know" maxLength={2000} />
              <label htmlFor={`${uid}file`}>Résumé or document (optional)</label>
              <input id={`${uid}file`} type="file" accept={ATTACH_ACCEPT} aria-describedby={`${uid}filehint`} onChange={(e) => {
                const picked = e.target.files?.[0];
                const seq = ++pickSeq.current;
                const input = e.target;
                setFileErr(null); setFile(null);
                if (!picked) return;
                setReading(true);
                readAttachment(picked)
                  .then((r) => { if (seq === pickSeq.current) setFile(r); })
                  .catch((x) => { if (seq === pickSeq.current) { setFileErr(x.message); input.value = ''; } })
                  .finally(() => { if (seq === pickSeq.current) setReading(false); });
              }} />
              <span id={`${uid}filehint`} className="muted">{file ? `Attached: ${file.name}` : 'PDF, Word, JPG or PNG, up to 5 MB.'}</span>
              {fileErr ? <div className="err">{fileErr}</div> : null}
              {/* Honeypot: people never see or fill this. */}
              <input name="hp_x9" value={f.hp_x9} onChange={(e) => setF({ ...f, hp_x9: e.target.value })} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: -9999 }} aria-hidden="true" />
              {err ? <div className="err">{err}</div> : null}
              {(() => {
                const missing = f.name.trim().length < 2 ? 'Enter your name.'
                  : !/@/.test(f.email) ? 'Enter your email.'
                    : f.phone.replace(/\D/g, '').length < 10 ? 'Enter a 10-digit phone number.'
                      : applicantProblem(ap);
                return (
                  <>
                    {missing ? <p className="muted" role="status" style={{ marginTop: 12 }}>{missing}</p> : null}
                    <div style={{ marginTop: 12 }}><button onClick={apply} disabled={state === 'sending' || reading || !!missing}>Send application</button></div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Seekers ─────────────────────────────────────────────────────────────────
function Available() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'send' | 'verify' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const uid = useId();
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  // Signing out starts over at the email, not at the last code screen.
  useEffect(() => { if (user === null) { setSentTo(null); setCode(''); } }, [user]);
  // Sign-in used to be an emailed link; one still sitting in an inbox lands here.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    window.history.replaceState({}, '', '/available');
    setErr('Sign-in links no longer work. Enter your email and we\'ll send you a code instead.');
  }, []);
  const send = async () => {
    setBusy('send'); setErr(null);
    try { await call('jobsSendSignInCode', { email: email.trim() }); setSentTo(email.trim()); setCode(''); } catch (e) { setErr(errText(e)); } finally { setBusy(null); }
  };
  const verify = async () => {
    setBusy('verify'); setErr(null);
    try {
      const r: any = await call('jobsConfirmSignInCode', { email: sentTo, code });
      await signInWithCustomToken(auth, r.token);
    } catch (e) { setErr(errText(e)); } finally { setBusy(null); }
  };
  if (user === undefined) return <div className="wrap muted">Loading…</div>;
  if (!user || !user.email) {
    return (
      <div className="wrap">
        <div className="card" style={{ maxWidth: 520 }}>
          <h1>Looking for laundry work?</h1>
          <p className="muted">Post a free profile: the kind of work you want, where, and when you're free. Laundromat owners on suds Connect can reach out. Your email and phone are never shown; owners' messages come to your inbox.</p>
          {!sentTo ? (
            <>
              <label htmlFor={`${uid}email`}>Your email</label><input id={`${uid}email`} aria-required="true" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              <div style={{ marginTop: 12 }}><button onClick={send} disabled={busy !== null || !/@/.test(email)}>{busy === 'send' ? 'Sending…' : 'Email me a code'}</button></div>
            </>
          ) : (
            <>
              <p className="muted">We sent a 6-digit code to <b>{sentTo}</b>. It expires in 10 minutes.</p>
              <label htmlFor={`${uid}code`}>Code</label>
              <input id={`${uid}code`} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" />
              <div className="row" style={{ marginTop: 12 }}>
                <button onClick={verify} disabled={busy !== null || code.length !== 6}>{busy === 'verify' ? 'Checking…' : 'Sign in'}</button>
                <button className="secondary" onClick={send} disabled={busy !== null}>Send a new code</button>
              </div>
              <p className="muted" style={{ marginTop: 10 }}><a href="#email" onClick={(e) => { e.preventDefault(); setSentTo(null); setErr(null); }}>Use a different email</a></p>
            </>
          )}
          {err ? <div className="err">{err}</div> : null}
        </div>
      </div>
    );
  }
  return <SeekerForm user={user} />;
}

function SeekerForm({ user }: { user: User }) {
  // No phone in the form: the PROVED one is saved (seekerSave ignores anything sent).
  const [f, setF] = useState<any>({ firstName: '', lastName: '', city: '', state: '', roles: [] as string[], availability: '', about: '' });
  // undefined while seekerMe loads; null when no phone has been proved yet.
  const [phone, setPhone] = useState<string | null | undefined>(undefined);
  const [oldPhone, setOldPhone] = useState<string | null>(null);
  const [changingPhone, setChangingPhone] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const uid = useId();
  const id = (k: string) => `${uid}${k}`;
  useEffect(() => {
    call('seekerMe').then((r) => {
      setPhone(r.verifiedPhone ?? null);
      if (!r.profile) return;
      setOldPhone(r.profile.phone || null);
      // Stored as "Mary Ann K.": the last word is the initial only when it looks like one.
      const words = String(r.profile.name ?? '').trim().split(/\s+/);
      const hasInitial = words.length > 1 && /^[A-Z]\.$/.test(words[words.length - 1]);
      setF({ firstName: (hasInitial ? words.slice(0, -1) : words).join(' '), lastName: hasInitial ? words[words.length - 1].slice(0, 1) : '', city: r.profile.city, state: r.profile.state, roles: r.profile.roles ?? [], availability: r.profile.availability ?? '', about: r.profile.about ?? '' });
      setStatus(r.profile.status);
    }).catch((e) => setErr(errText(e)));
  }, []);
  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try { const r = await call('seekerSave', f); setStatus(r.status); setMsg("Saved. We'll review it shortly; it stays up for 30 days."); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete your profile?')) return;
    setBusy(true); setErr(null);
    try { await call('seekerDelete'); } catch (e) { setErr(errText(e, "Your profile couldn't be deleted. Please try again.")); setBusy(false); return; }
    await signOut(auth);
  };
  if (phone === undefined && !err) return <div className="wrap muted">Loading…</div>;
  // Nothing goes on without a proved phone (seekerSave refuses without one too).
  if (!phone) {
    return (
      <div className="wrap">
        <VerifyPhone
          current={oldPhone}
          title="Verify your phone"
          intro="We text a 6-digit code to confirm your number. Owners never see it."
          onVerified={setPhone}
        />
        {err ? <div className="err">{err}</div> : null}
        <p className="muted" style={{ textAlign: 'center' }}>Signed in as {user.email} · <button className="secondary" onClick={() => signOut(auth)}>Sign out</button></p>
      </div>
    );
  }
  const toggle = (k: string) => setF((x: any) => ({ ...x, roles: x.roles.includes(k) ? x.roles.filter((r: string) => r !== k) : [...x.roles, k].slice(0, 4) }));
  return (
    <div className="wrap">
      <div className="card" style={{ maxWidth: 640 }}>
        <h1>Your profile</h1>
        <p className="muted">Signed in as {user.email}. Owners see your first name, last initial, city and what you write here. {status ? <b>Status: {status === 'active' ? 'live' : status === 'pending' ? 'in review' : status}</b> : null}</p>
        <div className="row">
          <div><label htmlFor={id('first')}>First name</label><input id={id('first')} aria-required="true" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></div>
          <div><label htmlFor={id('last')}>Last name (only the initial is shown)</label><input id={id('last')} value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></div>
        </div>
        <div className="row">
          <div><label htmlFor={id('city')}>City</label><input id={id('city')} aria-required="true" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
          <div><label htmlFor={id('state')}>State</label><select id={id('state')} aria-required="true" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}><option value="">—</option>{STATES.map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>
        <label id={id('roles')}>Work you want</label>
        <div className="chips" role="group" aria-labelledby={id('roles')}>{Object.entries(ROLES).map(([k, v]) => <button key={k} className={`chip ${f.roles.includes(k) ? 'on' : ''}`} onClick={() => toggle(k)}>{v}</button>)}</div>
        <label htmlFor={id('avail')}>When you're available</label><input id={id('avail')} value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} placeholder="Weekends, evenings after 5pm" maxLength={120} />
        <label htmlFor={id('about')}>About you</label><textarea id={id('about')} value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} maxLength={800} placeholder="Experience, languages, anything an owner should know" />
        <label htmlFor={id('phone')}>Phone (never shown)</label>
        <input id={id('phone')} value={formatPhone(phone)} readOnly aria-describedby={id('phonehint')} />
        <span id={id('phonehint')} className="muted">Verified · <a href="#phone" onClick={(e) => { e.preventDefault(); setChangingPhone(true); }}>Change</a></span>
        {changingPhone ? (
          <VerifyPhone
            current={phone}
            title="Change your phone"
            intro="We text a code to the new number. Save your profile afterwards to use it."
            onVerified={(p) => { setPhone(p); setChangingPhone(false); }}
            onCancel={() => setChangingPhone(false)}
          />
        ) : null}
        {err ? <div className="err">{err}</div> : null}
        {msg ? <div className="ok">{msg}</div> : null}
        <div className="row" style={{ marginTop: 14 }}>
          <button onClick={save} disabled={busy}>Save profile</button>
          <button className="secondary" onClick={() => signOut(auth)}>Sign out</button>
          <button className="danger" onClick={remove}>Delete profile</button>
        </div>
      </div>
    </div>
  );
}
